import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  Stack,
  CircularProgress,
  Backdrop,
  Divider,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Collapse,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { showSnackbar } from '../../components/Snackbar';
import ModulesRoles, { getAllModulesWithActions } from './ModulesRoles';
import { getToken } from '../../services/authAPIS';
import sidebarStructure from '../../components/Sidebar/SidebarStructure';
// import OTPVerificationModal from '../../components/OTPVerificationModal/OTPVerificationModal';

const normalizeActions = (actionsRaw) => {
  if (Array.isArray(actionsRaw)) return actionsRaw.map((a) => String(a));
  if (typeof actionsRaw === 'string') return [actionsRaw];
  return [];
};

const slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');

const makeModuleName = (groupLabel, itemLabel) => `${slugify(groupLabel)}.${slugify(itemLabel)}`;

const buildSidebarLabelMaps = (structure) => {
  const moduleMap = new Map();
  const groupMap = new Map();
  const sections = Array.isArray(structure) ? structure : [];

  sections.forEach((section) => {
    const groups = Array.isArray(section?.children) ? section.children : [];
    groups.forEach((group) => {
      const groupLabel = group?.label;
      if (!groupLabel) return;
      const groupSlug = slugify(groupLabel);
      if (groupSlug && !groupMap.has(groupSlug)) groupMap.set(groupSlug, groupLabel);

      const children = Array.isArray(group?.children) ? group.children : [];
      if (!children.length) {
        const key = makeModuleName(groupLabel, groupLabel);
        moduleMap.set(key, { groupLabel, itemLabel: groupLabel });
        return;
      }

      children.forEach((child) => {
        const childLabel = child?.label;
        if (!childLabel) return;
        const key = makeModuleName(groupLabel, childLabel);
        moduleMap.set(key, { groupLabel, itemLabel: childLabel });
      });
    });
  });

  return { moduleMap, groupMap };
};

const { moduleMap: sidebarModuleLabelMap, groupMap: sidebarGroupLabelMap } =
  buildSidebarLabelMaps(sidebarStructure);

const HUMANIZE_ACRONYMS = new Set(['hq', 'emi', 'tds', 'kyc', 'gst', 'id', 'api']);
const toTitle = (word) => {
  const w = String(word || '').trim();
  if (!w) return '';
  const lower = w.toLowerCase();
  if (HUMANIZE_ACRONYMS.has(lower)) return lower.toUpperCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

const humanizeSlug = (slug) => {
  const s = String(slug || '').trim();
  if (!s) return '';
  return s
    .split('-')
    .filter(Boolean)
    .map((w) => toTitle(w))
    .join(' ');
};

const splitModuleName = (name) => {
  const parts = String(name || '').split('.');
  const parent = parts[0] || '';
  const child = parts.length > 1 ? parts.slice(1).join('.') : '';
  return { parent, child };
};

const formatParentLabel = (parentSlug) => {
  const key = String(parentSlug || '').trim().toLowerCase();
  if (!key) return '';
  let label = sidebarGroupLabelMap.get(key) || humanizeSlug(key);
  if (key === 'accounts') label = 'Account';
  return label;
};

const formatChildLabel = (moduleKey) => {
  const normalized = String(moduleKey || '').trim().toLowerCase();
  if (!normalized) return '';
  const { parent, child } = splitModuleName(normalized);
  const mapped = sidebarModuleLabelMap.get(normalized);
  let childLabel = mapped?.itemLabel || humanizeSlug(child || parent);

  if (normalized === 'accounts.advance-payment') childLabel = 'Advanced Payments';

  return childLabel;
};

const buildModulesTree = (modules = []) => {
  const map = new Map();
  (Array.isArray(modules) ? modules : []).forEach((m) => {
    const full = String(m?.name || '').trim();
    if (!full) return;
    const { parent } = splitModuleName(full);
    const key = parent || full;
    const items = map.get(key) || [];
    items.push({ name: full, actions: normalizeActions(m?.actions) });
    map.set(key, items);
  });

  return Array.from(map.entries())
    .map(([parent, items]) => ({
      parent,
      items: items.sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.parent.localeCompare(b.parent));
};

export default function CreateRoles() {
  const history = useHistory();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [errors, setErrors] = useState({});
  const [roleType, setRoleType] = useState('other');
  const [customName, setCustomName] = useState('');
  const [prevOtherModules, setPrevOtherModules] = useState([]);
  const allModules = React.useMemo(() => getAllModulesWithActions(), []);
  const [modules, setModules] = useState(() => getAllModulesWithActions());
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteConfirmRole, setDeleteConfirmRole] = useState(null);
  const [viewRoleId, setViewRoleId] = useState('');
  const [viewRoleName, setViewRoleName] = useState('');
  const [viewRoleDetail, setViewRoleDetail] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewError, setViewError] = useState('');
  const [expandedParents, setExpandedParents] = useState({});
  // const [showOtpModal, setShowOtpModal] = useState(false);
  // const [isVerified, setIsVerified] = useState(false);

  const adminExists = React.useMemo(
    () => (roles || []).some((r) => String(r?.name || '').toLowerCase().trim() === 'admin'),
    [roles],
  );

  // Simplified Payload
  const buildPayload = () => ({
    name: roleType === 'admin' ? 'admin' : customName?.trim(),
    roleType: roleType,
    modules: modules && modules.length ? modules : undefined,
  });

  const resetForm = () => {
    const nextRoleType = adminExists ? 'other' : 'admin';
    setRoleType(nextRoleType);
    setCustomName('');
    setPrevOtherModules([]);
    setErrors({});
    setModules(nextRoleType === 'admin' ? allModules : []);
  };

  const validate = () => {
    const newErrors = {};

    if (roleType !== 'admin' && !customName.trim()) newErrors.customName = 'Name is required';
    if (!modules || modules.length === 0) {
      newErrors.modules = 'Select at least one module/action';
      setSnackbar({ open: true, message: newErrors.modules, severity: 'error' });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleActualSubmit = async () => {
    setError('');
    setSuccess('');
    setIsSubmitting(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/roleMasters`;
      const payload = buildPayload();
      const token = getToken();
      await axios.post(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      setSuccess('Role created successfully');
      setSnackbar({ open: true, message: 'Role created successfully', severity: 'success' });
      try { showSnackbar({ type: 'success', message: 'Role created successfully' }); } catch { }
      resetForm();
      fetchRoles();
    } catch (err) {
      const data = err?.response?.data || {};
      const msg = data?.message || err?.message || 'Failed to create';
      setError(msg);
      setSnackbar({ open: true, message: msg, severity: 'error' });
      try { showSnackbar({ type: 'error', message: msg }); } catch { }

      if (Array.isArray(data?.errors) && data.errors.length) {
        const fieldErrs = {};
        data.errors.forEach((e) => {
          const m = e?.msg || e?.message || '';
          const p = e?.path || '';
          if (p && m) fieldErrs[p] = m;
          try { if (m) showSnackbar({ type: 'error', message: m }); } catch { }
        });
        if (Object.keys(fieldErrs).length) setErrors((prev) => ({ ...prev, ...fieldErrs }));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    handleActualSubmit();
  };

  const handleRoleTypeChange = (e) => {
    const value = e.target.value;
    setRoleType(value);
    if (value === 'admin') {
      setPrevOtherModules(modules);
      setModules(allModules);
    } else {
      setModules(prevOtherModules);
    }
  };

  const handleEditRole = (role) => {
    const id = role?._id;
    const path = id ? `/app/editRoles/${id}` : '/app/editRoles';
    history.push(path, { role });
  };

  const handleDelete = async (role) => {
    const id = role?._id;
    if (!id) return;
    setIsDeleting(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/roleMasters/${id}`;
      const token = getToken();
      await axios.delete(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      setSnackbar({ open: true, message: 'Role deleted successfully', severity: 'success' });
      fetchRoles();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to delete role';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const openView = (role) => {
    const id = role?._id;
    if (!id) return;
    setViewRoleId(String(id));
    setViewRoleName(role?.name || '');
    setViewRoleDetail(role);
    setViewLoading(false);
    setViewError('');
  };

  const closeView = () => {
    setViewRoleId('');
    setViewRoleName('');
    setViewRoleDetail(null);
    setViewLoading(false);
    setViewError('');
    setExpandedParents({});
  };

  React.useEffect(() => {
    // No API fetch here; use the role object already available in state.
    if (!viewRoleId) return undefined;
    setViewLoading(false);
    setViewError('');
    return undefined;
  }, [viewRoleId]);

  const viewGroups = React.useMemo(() => buildModulesTree(viewRoleDetail?.modules), [viewRoleDetail?.modules]);

  React.useEffect(() => {
    if (!viewRoleDetail) return;
    if (viewGroups.length === 0) return;
    const next = {};
    viewGroups.forEach((g) => {
      next[g.parent] = true;
    });
    setExpandedParents(next);
  }, [viewGroups, viewRoleDetail]);

  const toggleParent = (parent) => {
    setExpandedParents((p) => ({ ...p, [parent]: !p[parent] }));
  };

  const fetchRoles = async () => {
    setRolesLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}/api/roleMasters`;
      const token = getToken();
      const res = await axios.get(url, {
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      const list = res?.data?.data || [];
      setRoles(Array.isArray(list) ? list : []);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to fetch roles';
      setSnackbar({ open: true, message: msg, severity: 'error' });
    } finally {
      setRolesLoading(false);
    }
  };

  React.useEffect(() => {
    fetchRoles();

    // Access control: Only Admin can access Role section
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const user = JSON.parse(storedUser);
        const role = (user.role || '').toLowerCase();
        // Mobile number for OTP verification
        if (role !== 'admin' && role !== 'superadmin') {
          setSnackbar({
            open: true,
            message: 'Access Denied: You do not have permission to access the Role section.',
            severity: 'error'
          });
          setTimeout(() => {
            history.push('/app/dashboard');
          }, 1500);
        }
      }
    } catch (err) {
      console.error('Error checking user role:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (location.state?.refreshEditRoles) {
      fetchRoles();
      const msg = location.state?.message || 'Role updated successfully';
      setSnackbar({ open: true, message: msg, severity: 'success' });
      history.replace(location.pathname, { ...location.state, refreshEditRoles: false, message: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  React.useEffect(() => {
    if (adminExists && roleType === 'admin') {
      setRoleType('other');
      setModules(prevOtherModules);
    }
  }, [adminExists, prevOtherModules, roleType]);

  if (rolesLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography color="text.secondary">Loading Roles...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        maxWidth: 1200,
        mx: 'auto',
      }}
    >
      {/* All Roles */}
      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 10px 30px rgba(15,23,42,0.06), 0 0 0 1px rgba(148,163,184,0.2)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            All Roles
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {rolesLoading && <CircularProgress size={20} />}
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleSubmit}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 2,
                boxShadow: 'none',
                '&:hover': { boxShadow: 'none' },
              }}
            >
              + Add Role
            </Button>
          </Box>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Role Type</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Updated</TableCell>
                <TableCell sx={{ fontWeight: 700, textAlign: 'right' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(roles || []).map((role) => (
                <TableRow key={role._id}>
                  <TableCell>{role.name || '-'}</TableCell>
                  <TableCell>{role.roleType || '-'}</TableCell>
                  <TableCell>{role.createdAt ? new Date(role.createdAt).toLocaleString() : '-'}</TableCell>
                  <TableCell>{role.updatedAt ? new Date(role.updatedAt).toLocaleString() : '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => openView(role)}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="primary" onClick={() => handleEditRole(role)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    {/* <IconButton
                      size="small"
                      color="error"
                      sx={{ ml: 1 }}
                      onClick={() => setDeleteConfirmRole(role)}
                      disabled={isDeleting}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton> */}
                  </TableCell>
                </TableRow>
              ))}
              {!rolesLoading && (!roles || roles.length === 0) && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      No roles found.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Box sx={{ mb: 2 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, fontSize: { xs: '0.7rem', md: '1rem' } }}
        >
          Create Roles
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', mt: 0.5 }}
        >
          Define a new role and assign permissions.
        </Typography>
      </Box>

      <Paper
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          boxShadow:
            '0 10px 30px rgba(15,23,42,0.06), 0 0 0 1px rgba(148,163,184,0.2)',
        }}
      >
        <form onSubmit={handleSubmit} noValidate>
          <Stack spacing={2} sx={{ mb: 2 }}>
            {error ? (
              <Alert severity="error" onClose={() => setError('')}>
                {error}
              </Alert>
            ) : null}
            {success ? (
              <Alert severity="success" onClose={() => setSuccess('')}>
                {success}
              </Alert>
            ) : null}
          </Stack>

          {/* Basic Details */}
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            Role Details
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Role Type"
                value={roleType}
                onChange={handleRoleTypeChange}
                fullWidth
              // disabled={adminExists}
              >
                {!adminExists ? <MenuItem value="admin">Admin</MenuItem> : null}
                <MenuItem value="subadmin">Sub Admin</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Grid>
            {roleType !== 'admin' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Custom Role / Name"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  fullWidth
                  error={!!errors.customName}
                  helperText={errors.customName}
                  placeholder={roleType === 'subadmin' ? "e.g. Regional Manager" : "e.g. Sales Executive"}
                />
              </Grid>
            )}
          </Grid>

          {/* Modules & Permissions */}
          <Divider sx={{ my: 3 }} />
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            Modules & Permissions
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            Select allowed modules and their actions for this role.
          </Typography>
          {errors.modules ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.modules}
            </Alert>
          ) : null}
          <ModulesRoles value={modules} onChange={setModules} disabled={roleType === 'admin'} />

          {/* Actions */}
          <Box
            sx={{
              mt: 3,
              display: 'flex',
              gap: 2,
              justifyContent: 'flex-end',
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={resetForm}
              disabled={isSubmitting}
            >
              Reset
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              onClick={handleSubmit}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: 999,
                px: 3,
              }}
            >
              {isSubmitting && (
                <CircularProgress
                  size={18}
                  sx={{ mr: 1, color: 'inherit' }}
                />
              )}
              {isSubmitting ? 'Saving…' : '+ Add Role'}
            </Button>
          </Box>
        </form>
      </Paper>

      <Backdrop open={isSubmitting || isDeleting} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
      >
        <Alert
          onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
          severity={snackbar.severity || 'info'}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={Boolean(viewRoleName)} onClose={closeView} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>Role Details</Typography>
            <Typography variant="body2" color="text.secondary">
              {viewRoleName}
            </Typography>
          </Box>
          {viewLoading ? <CircularProgress size={22} /> : null}
        </DialogTitle>
        <DialogContent dividers>
          {viewError ? <Alert severity="error">{viewError}</Alert> : null}

          {!viewError && viewLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading role permissions…
              </Typography>
            </Box>
          ) : null}

          {!viewLoading && !viewError ? (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Modules:{' '}
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {Array.isArray(viewRoleDetail?.modules) ? viewRoleDetail.modules.length : 0}
                </Box>
              </Typography>

              {viewGroups.length ? (
                <List dense>
                  {viewGroups.map((group) => {
                    const isOpen = Boolean(expandedParents[group.parent]);
                    return (
                      <Box key={group.parent}>
                        <ListItem
                          button
                          onClick={() => toggleParent(group.parent)}
                          sx={{
                            borderRadius: 2,
                            border: '1px solid rgba(148,163,184,0.35)',
                            mb: 1,
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography sx={{ fontWeight: 800 }}>
                                {formatParentLabel(group.parent)}
                              </Typography>
                            }
                            secondary={`${group.items.length} permissions`}
                          />
                        </ListItem>
                        <Collapse in={isOpen} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding dense sx={{ mb: 1 }}>
                            {group.items.map((m) => (
                              <ListItem
                                key={m.name}
                                sx={{
                                  pl: 6,
                                  py: 0.75,
                                  borderRadius: 2,
                                  '&:hover': { bgcolor: 'rgba(148,163,184,0.08)' },
                                }}
                              >
                                <ListItemText
                                  primary={
                                    <Typography sx={{ fontWeight: 700 }}>
                                      {formatChildLabel(m.name)}
                                    </Typography>
                                  }
                                />
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                  {(m.actions || []).length ? (
                                    m.actions.map((a) => {
                                      const aa = String(a || '').toLowerCase();
                                      const color =
                                        aa === 'read' ? 'primary' : aa === 'write' ? 'secondary' : 'default';
                                      return (
                                        <Chip
                                          key={`${m.name}:${a}`}
                                          size="small"
                                          label={String(a)}
                                          color={color}
                                          variant="outlined"
                                        />
                                      );
                                    })
                                  ) : (
                                    <Chip size="small" color="default" label="no actions" variant="outlined" />
                                  )}
                                </Box>
                              </ListItem>
                            ))}
                          </List>
                        </Collapse>
                      </Box>
                    );
                  })}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No modules assigned.
                </Typography>
              )}
            </>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeView}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteConfirmRole)}
        onClose={() => setDeleteConfirmRole(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ mb: 1.5 }}>
            Are you sure you want to delete this Role from masters ?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {deleteConfirmRole?.name ? `Role: ${deleteConfirmRole.name}` : null}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmRole(null)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              const role = deleteConfirmRole;
              setDeleteConfirmRole(null);
              handleDelete(role);
            }}
            disabled={isDeleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
